import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import Device from "device";
import { checkmarkCircle, refreshCircle } from "ionicons/icons";
import cyAttr from "lib/cyAttr";
import L10n from "localization";

export interface ICharge {
  id: string;
  amount: number;
  amount_refunded: number;
  created: number;
  currency: string;
  receipt_url: string;
  status: string;
}

export default function PaymentHistoryModal({
  dismiss,
  charges,
}: {
  dismiss: () => void;
  charges: ICharge[];
}) {
  const formatAmount = (amount: number, currency: string) => {
    const currencySymbol = currency === "usd" ? "$" : currency.toUpperCase();
    const formattedAmount = (amount / 100).toFixed(2);
    return `${currencySymbol}${formattedAmount}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleChargeClick = (receiptUrl: string) => {
    if (receiptUrl) {
      Device.openExternalLink(receiptUrl);
    }
  };

  return (
    <IonContent
      {...cyAttr("payment-history-modal")}
      style={
        { "--background": "var(--ion-color-light)" } as React.CSSProperties & {
          "--background": string;
        }
      }
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>{L10n.localize((s) => s.profile.paymentHistory)}</IonTitle>
          <IonButtons slot="primary">
            <IonButton onClick={dismiss}>{L10n.localize((s) => s.actions.close)}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonList style={{ marginTop: 16 }}>
        {charges.length === 0 ? (
          <IonItem lines="none">
            <IonLabel class="ion-text-center">
              <IonText color="medium">{L10n.localize((s) => s.profile.noPaymentHistory)}</IonText>
            </IonLabel>
          </IonItem>
        ) : (
          charges.map((charge, index) => (
            <IonItem
              key={charge.id}
              button
              detail
              onClick={() => handleChargeClick(charge.receipt_url)}
              lines={index === charges.length - 1 ? "none" : "inset"}
            >
              <IonLabel>
                <h2>
                  {formatAmount(charge.amount, charge.currency)}
                  {charge.amount_refunded > 0 && (
                    <span style={{ marginLeft: 8, color: "var(--ion-color-danger)" }}>
                      (-{formatAmount(charge.amount_refunded, charge.currency)})
                    </span>
                  )}
                </h2>
                <p>{formatDate(charge.created)}</p>
              </IonLabel>
              <IonNote slot="end">
                {charge.amount_refunded > 0 ? (
                  <IonIcon
                    icon={refreshCircle}
                    color="warning"
                    style={{ fontSize: "1.5rem" }}
                    title="Refunded"
                  />
                ) : charge.status === "succeeded" ? (
                  <IonIcon
                    icon={checkmarkCircle}
                    color="success"
                    style={{ fontSize: "1.5rem" }}
                    title="Succeeded"
                  />
                ) : (
                  <span role="img" aria-label={charge.status}>
                    {charge.status === "pending" ? "⏳" : "❓"}
                  </span>
                )}
              </IonNote>
            </IonItem>
          ))
        )}
      </IonList>
    </IonContent>
  );
}
